import React, { useCallback } from 'react';
import Sticky from 'react-stickynode';
import { selectShowCurrencySelection } from 'tradera/state/multi-currency/selectors';
import { HOVER_MENUS } from './hover-menu/constants';
import { onWindowLoad } from 'utils/on-ready';
import { getGeolocation } from 'tradera/state/member/actions';
import uiActions from 'tradera/state/ui/actions';
import {
    closeMessagingModal,
    closeNotificationOptinModal,
    closeSearchForm,
    openSearchForm
} from 'tradera/state/ui/reducer';
import TraderaLogo from '@tradera/blueprint/theme/logos/tradera_new.svg?jsx';
import TraderaAppLogo from '@tradera/blueprint/theme/logos/tradera-app-logo.svg?jsx';
import CartLink from 'tradera/components/site-header/cart-link';
import DropdownMenu from 'tradera/components/site-header/dropdown-menu';
import SignUpLink from 'tradera/components/site-header/sign-up-link';
import ALink from 'tradera/components/alink/alink';
import { Search } from 'tradera/legacy/static/components/search/search';
import PushNotificationModal from 'tradera/components/push-notification-modal/push-notification-modal';
import { LocalizationLink } from './localization-link';
import BuyingLink from './buying-link';
import { HowTraderaWorks } from './how-tradera-works';
import NewListingLink from './new-listing-link';
import { ProfileMenuLink } from './profile-menu-link';
import { SearchButton } from './search-button';

import { MessagingModal } from 'tradera/components/messaging/messaging-modal';
import WishlistLink from 'tradera/components/site-header/wishlist-link';
import OngoingAuctionsLink from 'tradera/components/site-header/ongoing-auctions-link';
import SellingLink from 'tradera/components/site-header/selling-link';
import { TraderaToastContainer } from 'tradera/components/toasts/tradera-toast-container';
import { showSuccessToast } from 'tradera/services/toasts';
import { CurrencyLink } from 'tradera/components/site-header/currency-link';
import { WishListSideSheet } from 'tradera/apps/tradera/components/wish-list-side-sheet/wish-list-side-sheet';
import { LocalizationRegionPicker } from './localization-region-picker';
import { LoaderLine } from 'tradera/components/loader/loader-line';
import { VerticalSeparator } from 'tradera/components/separator/separator';
import { SaveSearchButton } from 'tradera/apps/tradera/components/discover/components/search-results/save-search/save-search-button';
import { localStore } from 'tradera/utils/storage';
import { STORAGE_KEYS } from 'tradera/constants/storage';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { useIsTablet } from 'tradera/hooks/use-is-tablet';
import useIsDesktop from 'tradera/hooks/use-is-desktop';
import { useUrlLocalizer } from 'tradera/lang/translate';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import {
    getCartSummaryParams,
    useGetCartSummaryQuery
} from 'tradera/state/services/cart-api';
import { useCookieUtil } from 'tradera/hooks/use-cookie-util';
import { CART_ID } from 'tradera/constants/cookies';
import { MODAL_STATE } from 'tradera/constants/ui';

export const SiteHeader = ({ isPageLoading, inSPA }) => {
    const dispatch = useAppDispatch();

    const uiSlice = useAppSelector((state) => state.ui);
    const {
        showSearchForm,
        showHeaderSearch,
        messagingModal,
        notificationOptinModal
    } = uiSlice;
    const isLoggedIn = useAppSelector((state) => state.member.isLoggedIn);
    const showCurrencySelection = useAppSelector((state) =>
        selectShowCurrencySelection(state)
    );
    const pageName = useAppSelector((state) => state.page.type);

    const isMessagingEnabled = useIsFeatureEnabled('messaging');

    const isTablet = useIsTablet();
    const isDesktop = useIsDesktop();
    const isMobile = useIsMobile();
    const { toLocalizedUrl } = useUrlLocalizer();

    const [isSticky, setIsSticky] = React.useState(false);
    const [activeHoverMenu, setActiveHoverMenu] = React.useState(null);

    const cookieUtil = useCookieUtil();
    const cartIdCookieValue = cookieUtil.readCookie(CART_ID);

    const { data } = useGetCartSummaryQuery(
        getCartSummaryParams(cartIdCookieValue),
        { skip: !cartIdCookieValue }
    );

    const { totalCount: cartItemCount, items: cartItems } = data || {};
    const cartSum = cartItems?.reduce((acc, item) => acc + item.price, 0);

    const showMfaVerifiedMessage = useCallback(() => {
        if (!isLoggedIn) {
            return;
        }

        if (
            !document.referrer ||
            !document.referrer.includes(
                '/login/multifactorauthentication/enterverificationcode'
            )
        ) {
            return;
        }

        if (!localStore || !localStore.enabledByGdprSettings()) {
            return;
        }

        if (
            localStore.hasValueForKey(
                STORAGE_KEYS.HAS_SHOWED_MFA_VERIFICATION_MESSAGE
            )
        ) {
            return;
        }

        showSuccessToast('mfa_verify_success');
        localStore.setItem(
            STORAGE_KEYS.HAS_SHOWED_MFA_VERIFICATION_MESSAGE,
            true
        );
    }, [isLoggedIn]);

    React.useEffect(() => {
        dispatch(getGeolocation());
        if (isMessagingEnabled && isLoggedIn) {
            dispatch(uiActions.fetchMessagingNotifications());
        }
        showMfaVerifiedMessage();
        // Scroll up one pixel to show fixed header on mobile
        onWindowLoad(() => {
            // Toggle on sticky behavior on window load to force reflow and show sticky header on mobile & Firefox
            setIsSticky(true);
        });
    }, [dispatch, isLoggedIn, isMessagingEnabled, showMfaVerifiedMessage]);

    const handleClickSearch = () => {
        if (showSearchForm) {
            dispatch(closeSearchForm());
        } else {
            dispatch(openSearchForm());
        }
    };

    const handleMouseOverMenu = (menu) => {
        setActiveHoverMenu(menu);
    };

    const handleMouseOutMenu = () => {
        setActiveHoverMenu(null);
    };

    // Event handler for menu button:
    // Mobile: link, no menu open
    // Tablet: not a link, menu open
    // Desktop: link, menu open
    const handleMenuClick = (event, menu) => {
        if (
            isMobile &&
            menu !== HOVER_MENUS.LOCALIZATION &&
            menu !== HOVER_MENUS.CURRENCY
        ) {
            return;
        }

        // Prevent click for tablet to allow tap to open
        if (isTablet) {
            event.preventDefault();
        }
        // Force focus so touch devices can get a blur event
        event.target.focus();

        setActiveHoverMenu((current) => (current === menu ? false : menu));
    };

    const closeHoverMenu = () => {
        setActiveHoverMenu(null);
    };

    const handleRegisterClick = () => {
        if (!isLoggedIn) {
            const cookieName = 'signup_referral_path';
            const expireDate = new Date();
            const value = window.location.href;
            expireDate.setTime(expireDate.getTime() + 10 * 60 * 1000);
            cookieUtil.createCookie(cookieName, value, expireDate);
        }
    };

    const hasCartItems = cartItemCount > 0;
    const stickyEnabledOnPage = !pageName?.includes('MyTradera');

    const hideElements = !showHeaderSearch && pageName?.includes('StartPage');
    const transparentHeader = hideElements;

    const showAppLogoOnTinyScreen =
        (!isLoggedIn && hasCartItems) || !isLoggedIn;

    const showSaveSearch =
        (pageName?.includes('SearchResults') ||
            pageName?.includes('Category')) &&
        !isDesktop &&
        !isTablet;

    return (
        <>
            <Sticky
                enabled={stickyEnabledOnPage}
                top={0}
                enableTransforms={isSticky}
                activeClass="slim-header--sticky">
                <header
                    className={`slim-header d-hybrid-none container-fluid ${
                        showHeaderSearch ? '' : 'hide-elements'
                    }`}
                    id="site-header">
                    <div
                        className="row align-items-center mx-auto slim-header__content"
                        role="banner">
                        <DropdownMenu
                            className="navbar order-1 z-modal"
                            inSPA={inSPA}
                            isLoggedIn={isLoggedIn}
                            transparentHeader={transparentHeader}
                            tabIndex={1}
                        />
                        <div
                            className={`slim-header__logo order-2 mx-1 mx-md-2 ${
                                showAppLogoOnTinyScreen ? 'show-app-logo' : ''
                            } ${hasCartItems ? 'has-cart-items' : ''}`}
                            id="slim-header__logo">
                            <ALink
                                className="d-flex"
                                useLink={inSPA}
                                aria-label="Home"
                                href={toLocalizedUrl('/')}>
                                <TraderaAppLogo className="z-modal slim-header__logo--app" />
                                <TraderaLogo className="z-modal slim-header__logo--normal" />
                            </ALink>
                        </div>
                        <div
                            className={`col-12 order-4 order-md-3 col-md-auto d-none d-md-flex mt-2 mt-md-0 px-1 ${
                                showSearchForm && !hideElements ? 'd-flex' : ''
                            } slim-header__search `}
                            id="slim-header__search">
                            <Search inSPA={inSPA} />
                            {showSaveSearch && (
                                <SaveSearchButton
                                    type="icon"
                                    containerClassName="mr-n2"
                                    id={null}
                                />
                            )}
                        </div>
                        <nav
                            className="col col-md-auto order-3 order-md-4 navigation-links"
                            role="navigation">
                            <ul className="plain-list row ml-n1 justify-content-end flex-nowrap gap-x-md-1">
                                {!isLoggedIn && (
                                    <li className="slim-header__menu-option z-modal d-none d-lg-flex align-items-center">
                                        <LocalizationLink
                                            onClick={handleMenuClick}
                                            mouseOver={handleMouseOverMenu}
                                            mouseOut={handleMouseOutMenu}
                                            showMenu={
                                                activeHoverMenu ===
                                                HOVER_MENUS.LOCALIZATION
                                            }
                                            transparentHeader={
                                                transparentHeader
                                            }
                                        />
                                    </li>
                                )}
                                {!isLoggedIn && showCurrencySelection && (
                                    <>
                                        <li className="slim-header__menu-option z-modal d-none d-lg-flex align-items-center">
                                            <VerticalSeparator className="h-50" />
                                        </li>
                                        <li className="slim-header__menu-option z-modal d-none d-lg-flex align-items-center">
                                            <CurrencyLink
                                                onClick={handleMenuClick}
                                                mouseOver={handleMouseOverMenu}
                                                mouseOut={handleMouseOutMenu}
                                                onClickMenuOption={
                                                    closeHoverMenu
                                                }
                                                showMenu={
                                                    activeHoverMenu ===
                                                    HOVER_MENUS.CURRENCY
                                                }
                                                transparentHeader={
                                                    transparentHeader
                                                }
                                            />
                                        </li>
                                    </>
                                )}
                                <li
                                    className={`${
                                        isLoggedIn
                                            ? `order-lg-1 ${
                                                  hideElements
                                                      ? 'd-flex'
                                                      : 'd-none d-sm-flex'
                                              }`
                                            : 'd-none d-sm-flex'
                                    } slim-header__menu-option z-modal align-items-center`}>
                                    <NewListingLink
                                        transparentHeader={transparentHeader}
                                        isLoggedIn={isLoggedIn}
                                        isDesktop={isDesktop}
                                    />
                                </li>
                                {isLoggedIn && (
                                    <li
                                        className={`slim-header__menu-option z-header align-items-center d-md-none ${
                                            hasCartItems
                                                ? 'd-none d-sm-flex'
                                                : 'd-flex'
                                        }`}>
                                        <OngoingAuctionsLink
                                            transparentHeader={
                                                transparentHeader
                                            }
                                        />
                                    </li>
                                )}
                                {!hideElements && (
                                    <li className="slim-header__menu-option slim-header__menu-option-search d-flex d-md-none align-items-center">
                                        <SearchButton
                                            showSearch={showSearchForm}
                                            onClick={handleClickSearch}
                                        />
                                    </li>
                                )}
                                <li className="slim-header__menu-option z-modal d-flex align-items-center">
                                    {isLoggedIn ? (
                                        <WishlistLink
                                            onClick={handleMenuClick}
                                            mouseOver={handleMouseOverMenu}
                                            mouseOut={handleMouseOutMenu}
                                            transparentHeader={
                                                transparentHeader
                                            }
                                            showMenu={
                                                activeHoverMenu ===
                                                HOVER_MENUS.WISHLIST
                                            }
                                        />
                                    ) : (
                                        <HowTraderaWorks
                                            mouseOver={handleMouseOverMenu}
                                            mouseOut={handleMouseOutMenu}
                                            transparentHeader={
                                                transparentHeader
                                            }
                                            showMenu={
                                                activeHoverMenu ===
                                                HOVER_MENUS.HOW_TRADERA_WORKS
                                            }
                                        />
                                    )}
                                </li>
                                {isLoggedIn && (
                                    <li className="slim-header__menu-option z-modal d-none d-md-flex align-items-center">
                                        <BuyingLink
                                            onClick={handleMenuClick}
                                            mouseOver={handleMouseOverMenu}
                                            mouseOut={handleMouseOutMenu}
                                            transparentHeader={
                                                transparentHeader
                                            }
                                            showMenu={
                                                activeHoverMenu ===
                                                HOVER_MENUS.BUY
                                            }
                                        />
                                    </li>
                                )}
                                {isLoggedIn && (
                                    <li className="slim-header__menu-option z-modal d-none d-md-flex align-items-center">
                                        <SellingLink
                                            onClick={handleMenuClick}
                                            mouseOver={handleMouseOverMenu}
                                            mouseOut={handleMouseOutMenu}
                                            transparentHeader={
                                                transparentHeader
                                            }
                                            showMenu={
                                                activeHoverMenu ===
                                                HOVER_MENUS.SELL
                                            }
                                        />
                                    </li>
                                )}

                                {hasCartItems && (
                                    <li className="slim-header__menu-option z-modal d-flex align-items-center">
                                        <CartLink
                                            formattedCartItems={cartItemCount}
                                            cartSum={cartSum}
                                            transparentHeader={
                                                transparentHeader
                                            }
                                        />
                                    </li>
                                )}
                                <li className="slim-header__menu-option z-header d-flex align-items-center">
                                    <ProfileMenuLink
                                        transparentHeader={transparentHeader}
                                    />
                                </li>
                                {!isLoggedIn && (
                                    <li className="slim-header__menu-option z-header d-flex align-items-center ml-1 ml-md-0">
                                        <SignUpLink
                                            clickAction={handleRegisterClick}
                                            transparentHeader={
                                                transparentHeader
                                            }
                                        />
                                    </li>
                                )}
                            </ul>
                        </nav>
                    </div>
                </header>
                {messagingModal.open === MODAL_STATE.OPEN && (
                    <MessagingModal
                        onModalClosed={() => dispatch(closeMessagingModal())}
                        {...messagingModal}
                    />
                )}
                {notificationOptinModal.open === MODAL_STATE.OPEN && (
                    <PushNotificationModal
                        closeModal={() =>
                            dispatch(closeNotificationOptinModal())
                        }
                        modalType={notificationOptinModal.modalType}
                        gaLabel={notificationOptinModal.gaLabel}
                        title={notificationOptinModal.title}
                        description={notificationOptinModal.description}
                        tagGroups={notificationOptinModal.tagGroups}
                    />
                )}
                <TraderaToastContainer />
                <LoaderLine isActive={isPageLoading} />
            </Sticky>
            <LocalizationRegionPicker />
            <WishListSideSheet />
        </>
    );
};
